import { useState } from 'react'
import { Thermometer, Clock, Scale, Droplets, Utensils, RotateCcw } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import type { BrewingInstructions } from '@/entities/product/model/types'

export interface BrewingParams {
  /**
   * Температура воды (°C или строка с описанием)
   */
  temperature?: number | string
  /**
   * Время заваривания (секунды или строка с описанием)
   */
  time?: number | string
  /**
   * Пропорции (граммы чая на мл воды)
   */
  ratio?: string
  /**
   * Рекомендации по посуде
   */
  teaware?: string
  /**
   * Количество проливов
   */
  infusions?: number
}

export interface ProductBrewingProps {
  /**
   * Параметры заваривания (старый формат)
   */
  params?: BrewingParams
  /**
   * Инструкции заваривания (новый формат с gongfu/western)
   */
  brewing?: BrewingInstructions
}

type BrewingMethod = 'gongfu' | 'western'

/**
 * Виджет: Блок параметров заваривания
 * 
 * Поддерживает два способа заваривания:
 * - Гунфу ча (традиционный китайский)
 * - Европейский способ
 */
export function ProductBrewing({ params, brewing }: ProductBrewingProps) {
  // Определяем доступные методы
  const hasGongfu = brewing?.gongfu && (brewing.gongfu.temperature || brewing.gongfu.time || brewing.gongfu.amount)
  const hasWestern = brewing?.western && (brewing.western.temperature || brewing.western.time || brewing.western.amount)
  const hasLegacy = params && (params.temperature || params.time || params.ratio)
  
  const availableMethods: BrewingMethod[] = []
  if (hasGongfu) availableMethods.push('gongfu')
  if (hasWestern) availableMethods.push('western')
  
  const [activeMethod, setActiveMethod] = useState<BrewingMethod>(availableMethods[0] || 'gongfu')
  
  // Если нет данных о заваривании
  if (!hasGongfu && !hasWestern && !hasLegacy) {
    return null
  }
  
  // Получаем данные активного метода
  const getMethodData = () => {
    if (hasGongfu || hasWestern) {
      const data = activeMethod === 'gongfu' ? brewing?.gongfu : brewing?.western
      return {
        temperature: data?.temperature,
        time: data?.time,
        amount: data?.amount,
      }
    }
    // Legacy формат
    return {
      temperature: params?.temperature,
      time: params?.time,
      amount: params?.ratio,
    }
  }
  
  const methodData = getMethodData()
  
  const methodLabels = {
    gongfu: { title: 'Пролив', subtitle: 'традиционный' },
    western: { title: 'Настаивание', subtitle: 'классический' },
  }
  
  return (
    <div className="px-4 py-6 space-y-4">
      <h2 className="text-xl font-semibold text-ink dark:text-white">Заваривание</h2>
      
      {/* Переключатель методов (только если есть оба) */}
      {availableMethods.length > 1 && (
        <div className="flex p-1 bg-gray-100 dark:bg-dark-elevated rounded-xl">
          {availableMethods.map((method) => (
            <button
              key={method}
              type="button"
              onClick={() => setActiveMethod(method)}
              className={cn(
                'flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200',
                activeMethod === method
                  ? 'bg-white dark:bg-dark-card text-ink dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              )}
            >
              <span className="block">{methodLabels[method].title}</span>
              <span className="block text-xs font-normal opacity-60">{methodLabels[method].subtitle}</span>
            </button>
          ))}
        </div>
      )}
      
      {/* Параметры заваривания */}
      <div className="grid grid-cols-3 gap-3">
        {/* Температура */}
        {methodData.temperature && (
          <div className="p-4 bg-white dark:bg-dark-card rounded-2xl border border-silk dark:border-dark-border text-center">
            <div className="flex items-center justify-center mb-2">
              <Thermometer className="h-5 w-5 text-primary dark:text-primary-light" />
            </div>
            <div className="text-2xl font-bold text-ink dark:text-white tabular-nums">
              {methodData.temperature}
              <span className="text-base font-normal">°</span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mt-1">
              температура
            </div>
          </div>
        )}
        
        {/* Время */}
        {methodData.time && (
          <div className="p-4 bg-white dark:bg-dark-card rounded-2xl border border-silk dark:border-dark-border text-center">
            <div className="flex items-center justify-center mb-2">
              <Clock className="h-5 w-5 text-primary dark:text-primary-light" />
            </div>
            <div className="text-2xl font-bold text-ink dark:text-white tabular-nums">
              {methodData.time}
              <span className="text-base font-normal">{activeMethod === 'western' ? ' мин' : ' с'}</span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mt-1">
              время
            </div>
          </div>
        )}
        
        {/* Количество */}
        {methodData.amount && (
          <div className="p-4 bg-white dark:bg-dark-card rounded-2xl border border-silk dark:border-dark-border text-center">
            <div className="flex items-center justify-center mb-2">
              <Scale className="h-5 w-5 text-primary dark:text-primary-light" />
            </div>
            <div className="text-2xl font-bold text-ink dark:text-white tabular-nums">
              {methodData.amount}
              <span className="text-base font-normal">г</span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mt-1">
              {activeMethod === 'western' ? 'на 1л' : 'на 100мл'}
            </div>
          </div>
        )}
      </div>
      
      {/* Подсказка для гунфу */}
      {activeMethod === 'gongfu' && hasGongfu && (
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          Проливной метод: короткие заварки, много проливов
        </p>
      )}
    </div>
  )
}


